import { createLocalStorageCarrier } from '@/lib/local-storage-carrier'
import { STORAGE_KEYS } from '@/lib/pwa-storage'

/**
 * Único carrier del alta en curso en el cliente: lo que el sync de cuenta
 * (`contexts/auth-account-sync.ts`) tiene que mandar y que no cabe en el estado
 * de React porque el OAuth web recarga la página.
 *
 * - `source`: qué método de acceso inició el intento (decide qué evento de
 *   completado se emite tras volver del OAuth).
 * - `referredByCode`: el `?ref` de la landing o el código pegado a mano
 *   (ADR-013). Entrada NO confiable: aquí solo se acota; la validación real es
 *   del servidor, en el sellado.
 * - `marketingConsent`: la casilla del registro al iniciar el intento (ADR-047).
 *   Solo se guarda marcada.
 *
 * Cada parte se escribe cuando el usuario la decide y nunca pisa las demás; el
 * sync de sign-in la lee una vez y la borra entera al terminar.
 */
export const PENDING_AUTH_SOURCES = ['apple', 'google', 'otp'] as const

export type PendingAuthSource = (typeof PENDING_AUTH_SOURCES)[number]

export interface PendingSignup {
  source?: PendingAuthSource
  referredByCode?: string
  marketingConsent?: boolean
}

const MAX_REFERRAL_CODE_LENGTH = 64

function isPendingAuthSource(value: unknown): value is PendingAuthSource {
  return (PENDING_AUTH_SOURCES as readonly unknown[]).includes(value)
}

export function isPlausibleReferralCode(code: unknown): code is string {
  return typeof code === 'string' && code.length > 0 && code.length <= MAX_REFERRAL_CODE_LENGTH
}

function parsePendingSignup(stored: unknown): PendingSignup | null {
  if (typeof stored !== 'object' || stored === null) return null

  const { source, referredByCode, marketingConsent } = stored as Record<string, unknown>
  return {
    ...(isPendingAuthSource(source) ? { source } : {}),
    ...(isPlausibleReferralCode(referredByCode) ? { referredByCode } : {}),
    ...(marketingConsent === true ? { marketingConsent: true } : {})
  }
}

const carrier = createLocalStorageCarrier(STORAGE_KEYS.PENDING_SIGNUP, parsePendingSignup)

export function readPendingSignup(): PendingSignup {
  return carrier.read() ?? {}
}

/** Mezcla `patch` con lo guardado; un campo a `undefined` se quita. */
function updatePendingSignup(patch: PendingSignup): void {
  const next = parsePendingSignup({ ...readPendingSignup(), ...patch }) ?? {}

  if (Object.keys(next).length === 0) {
    carrier.clear()
  } else {
    carrier.write(next)
  }
}

export function rememberAuthSource(source: PendingAuthSource): void {
  updatePendingSignup({ source })
}

/** Un intento que no arrancó olvida su método, pero no el resto del alta (p. ej. el `?ref`). */
export function forgetAuthSource(): void {
  updatePendingSignup({ source: undefined })
}

/** La casilla tal cual al iniciar el intento: desmarcada, quita la de un intento anterior. */
export function rememberMarketingConsent(checked: boolean): void {
  updatePendingSignup({ marketingConsent: checked || undefined })
}

/** Un código no plausible (vacío, >64) se ignora: nunca borra uno válido ya guardado. */
export function rememberReferralCode(code: string): void {
  if (isPlausibleReferralCode(code)) {
    updatePendingSignup({ referredByCode: code })
  }
}

export function forgetReferralCode(): void {
  updatePendingSignup({ referredByCode: undefined })
}

export function clearPendingSignup(): void {
  carrier.clear()
}
